import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, TextField, Radio, InputAdornment, useTheme, CircularProgress, Snackbar, Alert, styled, Paper, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLoanContext } from '../LoanContext';
import { usePaymentsValidation } from '../../../hooks/useValidation';
import { formatAmount } from '../../../utils/formatters';
import { formatDateToDDMMYYYY, formatDateForAPI } from '../../../utils/dateUtils';
import PrimaryButton from '../../../components/styled/PrimaryButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PAYMENT_MODES, type PaymentMode } from '../../../constants/paymentModes';
import { BRAND_TABLE_TEXT_COLOR } from '../../../styles/tableColors';


const PaymentsSummaryCard = styled(Paper)(({ theme }) => ({
  background: theme.palette.mode === 'dark' ? '#1A1A1A' : '#f5f5f5',
  borderRadius: '12px',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
}));

const FooterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));
interface Payments {
    id: string,
    name: string,
    loanAccountId: string,
    totalOutstandingDueAmount: number,
    totalOutstandingAsOfDate: number,
    paymentDate: Date,
    amount: number;
    invalidState: string;
    status:string;
  
}

const PaymentsTab = () => {
  const { loan, refreshTrigger } = useLoanContext();
  const { loanId } = useParams(); // Get loanId directly from URL params
  const theme = useTheme();
  const [payments , setPayments ] = useState<Payments[]>([])
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState<PaymentMode>(PAYMENT_MODES.PARTIAL);
  const [paymentDate] = useState<Date | null>(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');


  const navigate = useNavigate();
  const { validatePaymentsForm } = usePaymentsValidation();
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [fullPayoffNoticeOpen, setFullPayoffNoticeOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Memoize fetchPaymentData to prevent recreation on every render
  const fetchPaymentData = useCallback((loanId: string, targetDate?: string | Date | null) => {
    setLoading(true);
    setError('');
  
    // Validate loanId to prevent SSRF
    if (!loanId || typeof loanId !== 'string' || loanId.length === 0) {
      setError('Invalid loan ID');
      setLoading(false);
      return;
    }

    // Use URLSearchParams to safely construct the URL
    const params = new URLSearchParams();
    params.append('loanId', loanId);
    if (targetDate) {
      params.append('paymentDate', formatDateForAPI(targetDate));
    }


    axios
      .get(`/api/payments?${params.toString()}`, {
        withCredentials: true,
        timeout: 10000, // 10 second timeout to prevent hanging
      })
      .then((res) => {
        setPayments(res.data);
        setLoading(false);
  
        // Payment date is now always set to today's date by default
      })
      .catch((err) => {
        if (err.code === 'ECONNABORTED') {
          setError('Request timeout - please try again.');
        } else {
          setError(err.response?.data?.error || 'Failed to fetch payments.');
        }
        setLoading(false);
      });
  }, []); // Remove paymentDate dependency to prevent recreation
  
  // Single useEffect for initial data fetch and refresh trigger
  useEffect(() => {
    if (!loanId || !loan) return; // wait until both ready; no premature errors
    fetchPaymentData(loanId);
  }, [loanId, loan, fetchPaymentData, refreshTrigger]);
  
  // Separate useEffect for payment date changes
  useEffect(() => {
    // Remove this useEffect since the backend doesn't use paymentDate parameter
    // and it's causing unnecessary API calls that hang
    // if (loanId && paymentDate && payments.length > 0) {
    //   // Format the date properly to avoid API issues
    //   const formattedDate = paymentDate instanceof Date ? paymentDate.toISOString().split('T')[0] : paymentDate;
    //   fetchPaymentData(loanId, formattedDate);
    // }
  }, [paymentDate, loanId, fetchPaymentData]);
  
  // Update amount when toggling partial vs due
  useEffect(() => {
    if (paymentMode === PAYMENT_MODES.DUE && loan) {
      setPaymentAmount(
        loan?.amountDueTillCurrent !== undefined
          ? Number(loan?.amountDueTillCurrent).toFixed(2)
          : ''
      );
    } else if (paymentMode === PAYMENT_MODES.FULL_PAYOFF && loan) {
      setPaymentAmount(
        loan?.payOffAmountAsOfToday !== undefined
          ? Number(loan?.payOffAmountAsOfToday).toFixed(2)
          : ''
      );
    } else if (paymentMode === PAYMENT_MODES.PARTIAL) {
      setPaymentAmount('');
    }
  }, [paymentMode, loan]);

  // Add debugging for render conditions

  if (!loanId || !loan) {
    return (
      <Box textAlign="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box textAlign="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ mt: 2 }}>
        {error}
      </Typography>
    );
  }

  const dueAmount = Number(loan?.amountDueTillCurrent || 0);
  const isDueModeWithZeroDue = paymentMode === PAYMENT_MODES.DUE && dueAmount <= 0;
  const proceedDisabledMessage = 'Sorry, no remaining Dues. If you wish to partially or fully pay off the loan, please use "Partial Pre-Payment" or "Ful Pay Off" options. Thank you.';

  const navigateToPaymentMethod = () => {
    if (loanId && typeof loanId === 'string' && loanId.length > 0) {
      const params = new URLSearchParams();
      if (paymentAmount) {
        params.append('amount', paymentAmount);
      }
      if (paymentDate) {
        params.append('date', formatDateForAPI(paymentDate));
      }
      
      const queryString = params.toString();
      const url = `/loans/${encodeURIComponent(loanId)}/payment-method${queryString ? `?${queryString}` : ''}`;
      navigate(url);
    }
  };

  const handleMakePayment = () => {
    const paymentAmt = paymentAmount;
    const outstanding = Number(loan?.payOffAmountAsOfToday) || 0;
    const loanStatus = payments[0]?.status;
    const backendStatus = payments[0]?.invalidState;

    const validationErrors = validatePaymentsForm(paymentAmt, outstanding, loanStatus, backendStatus);

    // Show toast for the first error found (priority: backendState, loanStatus, paymentAmount)
    if (validationErrors.backendState) {
      setToastMessage(validationErrors.backendState);
      setToastOpen(true);
      return;
    }
    if (validationErrors.loanStatus) {
      setToastMessage(validationErrors.loanStatus);
      setToastOpen(true);
      return;
    }
    if (validationErrors.paymentAmount) {
      setToastMessage(validationErrors.paymentAmount);
      setToastOpen(true);
      return;
    }

    if (paymentMode === PAYMENT_MODES.FULL_PAYOFF) {
      setConfirmDialogOpen(true);
      return;
    }

    navigateToPaymentMethod();
  };

  return (
    <Box sx={{ width: '100%', alignSelf: 'flex-start' }}>
      <Typography variant="h6" sx={{textAlign: 'left', fontWeight: 600, color: BRAND_TABLE_TEXT_COLOR }}>Summary</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            p: 2,
          }}
        >
          <Typography
            sx={{
              color: BRAND_TABLE_TEXT_COLOR,
              fontFamily: "'Libre Franklin', sans-serif",
              fontWeight: 700,
            }}
          >
            For any issues please contact the Momenta Finance team for assistance
          </Typography>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            p: 2,
          }}
        >
          <Typography
            sx={{
              color: BRAND_TABLE_TEXT_COLOR,
              fontFamily: "'Libre Franklin', sans-serif",
              fontWeight: 700,
            }}
          >
            Please note that if you are making a payment within 7 days of an upcoming payment that we are unable to stop your direct debit from being processed
          </Typography>
        </Paper>
      </Box>
      <PaymentsSummaryCard sx={{ width: '100%', mx: 0, mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Total Outstanding Due Amount */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography
                sx={{
                  color: BRAND_TABLE_TEXT_COLOR,
                  fontWeight: 500,
                }}
              >
                Total Outstanding Due Amount
              </Typography>
              <Tooltip
                title="Sum of all unpaid Bills"
                arrow
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: 16,
                    color: theme.palette.text.secondary,
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            </Box>
            <Typography sx={{ color: BRAND_TABLE_TEXT_COLOR }}>{formatAmount(loan?.amountDueTillCurrent || 0)}</Typography>
          </Box>

          {/* Total Outstanding (As of date) */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography
                sx={{
                  color: BRAND_TABLE_TEXT_COLOR,
                  fontWeight: 500,
                }}
              >
                Total Outstanding (As of date)
              </Typography>
              <Tooltip
                title="Total unpaid Balance"
                arrow
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: 16,
                    color: theme.palette.text.secondary,
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            </Box>
            <Typography sx={{ color: BRAND_TABLE_TEXT_COLOR }}>{formatAmount(loan?.payOffAmountAsOfToday || 0)}</Typography>
          </Box>
        </Box>
      </PaymentsSummaryCard>
      <Box display="flex" gap={4} my={3} sx={{ justifyContent: 'flex-start' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Radio
            checked={paymentMode === PAYMENT_MODES.DUE}
            onChange={() => setPaymentMode(PAYMENT_MODES.DUE)}
            value={PAYMENT_MODES.DUE}
            size="small"
            sx={{
              color: '#fff',
              '&.Mui-checked': { color: '#fff' },
            }}
          />
          <Typography sx={{ textAlign: 'left', color: '#fff', fontWeight: 500 }}>Due Payment</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Radio
            checked={paymentMode === PAYMENT_MODES.PARTIAL}
            onChange={() => setPaymentMode(PAYMENT_MODES.PARTIAL)}
            value={PAYMENT_MODES.PARTIAL}
            size="small"
            sx={{
              color: '#fff',
              '&.Mui-checked': { color: '#fff' },
            }}
          />
          <Typography sx={{ textAlign: 'left', color: '#fff', fontWeight: 500 }}>Partial Pre-Payment</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Radio
            checked={paymentMode === PAYMENT_MODES.FULL_PAYOFF}
            onChange={() => {
              setPaymentMode(PAYMENT_MODES.FULL_PAYOFF);
              setFullPayoffNoticeOpen(true);
            }}
            value={PAYMENT_MODES.FULL_PAYOFF}
            size="small"
            sx={{
              color: '#fff',
              '&.Mui-checked': { color: '#fff' },
            }}
          />
          <Typography sx={{ textAlign: 'left', color: '#fff', fontWeight: 500 }}>Full Pay off</Typography>
          <Tooltip title="Full Pay off means that you wish to pay the entire loan balance and close this loan" arrow>
            <InfoOutlinedIcon
              sx={{
                fontSize: 16,
                color: '#fff',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        </Box>
      </Box>

      <PaymentsSummaryCard sx={{ width: '100%', mx: 0, mb: 4 }}>
        <Box mb={4}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left', color: BRAND_TABLE_TEXT_COLOR, fontWeight: 500 }}>
            {paymentMode === PAYMENT_MODES.FULL_PAYOFF ? 'Payoff Amount' : 'Enter Amount'}
          </Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Enter Amount"
            value={paymentAmount}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (paymentMode === PAYMENT_MODES.PARTIAL) setPaymentAmount(e.target.value);
            }}
            InputProps={{
              readOnly: paymentMode !== PAYMENT_MODES.PARTIAL,
              startAdornment: (
                <InputAdornment position="start" sx={{ color: BRAND_TABLE_TEXT_COLOR }}>£
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-input': {
                color: `${BRAND_TABLE_TEXT_COLOR} !important`,
                background: theme.palette.mode === 'dark' ? '#232228' : '#fff',
                fontSize: '16px !important',
                padding: '12px 14px',
                '&::placeholder': {
                  color: BRAND_TABLE_TEXT_COLOR,
                  opacity: 1,
                },
              },
              background: theme.palette.mode === 'dark' ? '#232228' : '#fff',
              borderRadius: 1,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.mode === 'dark' ? '#666' : '#999',
                borderWidth: '1px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.mode === 'dark' ? '#888' : '#666',
              },
              '&:focus .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.mode === 'dark' ? '#888' : '#1976d2',
              },
            }}
          />
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left', color: BRAND_TABLE_TEXT_COLOR, fontWeight: 500 }}>
            {paymentMode === PAYMENT_MODES.FULL_PAYOFF ? 'Payoff Date' : 'Payment Date'}
          </Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            disabled
            value={paymentDate ? formatDateToDDMMYYYY(paymentDate) : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ color: BRAND_TABLE_TEXT_COLOR }}>📅</Box>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-input': {
                color: `${BRAND_TABLE_TEXT_COLOR} !important`,
                background: theme.palette.mode === 'dark' ? '#232228' : '#fff',
                fontSize: '16px !important',
                padding: '12px 14px',
                cursor: 'default',
              },
              background: theme.palette.mode === 'dark' ? '#232228' : '#fff',
              borderRadius: 1,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.mode === 'dark' ? '#666' : '#999',
                borderWidth: '1px',
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: BRAND_TABLE_TEXT_COLOR,
                color: BRAND_TABLE_TEXT_COLOR,
                opacity: 1,
              },
              '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.mode === 'dark' ? '#666' : '#999',
              },
            }}
          />
        </Box>
      </PaymentsSummaryCard>

      <FooterContainer>
        <PrimaryButton
          variant="outlined"
          sx={{ minWidth: 120 }}
          onClick={() => {
            if (loanId) {
              navigate(`/loans/${encodeURIComponent(loanId)}/summary`);
            }
          }}
        >
          Back
        </PrimaryButton>
        <Tooltip
          title={isDueModeWithZeroDue ? proceedDisabledMessage : ''}
          arrow
          disableHoverListener={!isDueModeWithZeroDue}
        >
          <span>
            <PrimaryButton onClick={handleMakePayment} disabled={isDueModeWithZeroDue}>
              Proceed
            </PrimaryButton>
          </span>
        </Tooltip>
      </FooterContainer>
      <Dialog
        open={fullPayoffNoticeOpen}
        disableEscapeKeyDown
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setFullPayoffNoticeOpen(false);
        }}
        aria-labelledby="full-payoff-legal-fees-title"
      >
        <DialogTitle id="full-payoff-legal-fees-title">Notice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please note that any legal fees due may not be included in this figure and may still be due
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <PrimaryButton onClick={() => setFullPayoffNoticeOpen(false)}>
            I Understand
          </PrimaryButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="full-payoff-confirmation-title"
      >
        <DialogTitle id="full-payoff-confirmation-title">Confirm Full Payoff</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action will repay the entire loan balance and close your loan. Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <PrimaryButton variant="outlined" onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </PrimaryButton>
          <PrimaryButton
            onClick={() => {
              setConfirmDialogOpen(false);
              navigateToPaymentMethod();
            }}
          >
            Proceed
          </PrimaryButton>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity="error" sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentsTab;